import { Layout } from "antd";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import notify from "../../Notification";
import {
  disableButton,
  updateWindowWidth,
  userSignOut,
} from "../../appRedux/actions";
import {
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
} from "../../constants/ThemeSetting";
import App from "../../routes/index";
import {
  initExportStream,
  appendExportChunk,
  finalizeExportStream,
} from "../../util/CSVandExcelDownload";
import {
  generateFormattedData,
  generateWithdrawReportFormattedData,
} from "../../util/DownloadFormattedData";
import {
  handleSocketEvent,
  handleSocketEvent2,
  handleSocketEvent3,
} from "../../util/Socket/socketEventHandler";
import { footerText } from "../../util/config";
import AboveHeader from "../Topbar/AboveHeader/index";
import BelowHeader from "../Topbar/BelowHeader/index";
import HorizontalDark from "../Topbar/HorizontalDark/index";
import HorizontalDefault from "../Topbar/HorizontalDefault/index";
import InsideHeader from "../Topbar/InsideHeader/index";
import NoHeaderNotification from "../Topbar/NoHeaderNotification/index";
import Topbar from "../Topbar/index";
import AppSidebar from "./AppSidebar";
import { depositSound, socket } from "../../util/Socket/socketClient";
import useIdleLogout from "../../util/useIdleLogout";

const { Content, Footer } = Layout;

const HorizontalDefaultMemoized = React.memo(HorizontalDefault);
const HorizontalDarkMemoized = React.memo(HorizontalDark);
const InsideHeaderMemoized = React.memo(InsideHeader);
const AboveHeaderMemoized = React.memo(AboveHeader);
const BelowHeaderMemoized = React.memo(BelowHeader);
const TopbarMemoized = React.memo(Topbar);
const NoHeaderNotificationMemoized = React.memo(NoHeaderNotification);

const getContainerClass = (navStyle) => {
  switch (navStyle) {
    case NAV_STYLE_DARK_HORIZONTAL:
      return "gx-container-wrap";
    case NAV_STYLE_DEFAULT_HORIZONTAL:
      return "gx-container-wrap";
    case NAV_STYLE_INSIDE_HEADER_HORIZONTAL:
      return "gx-container-wrap";
    case NAV_STYLE_BELOW_HEADER:
      return "gx-container-wrap";
    case NAV_STYLE_ABOVE_HEADER:
      return "gx-container-wrap";
    default:
      return "";
  }
};

const getNavStyles = (navStyle, handleSoundUpdate) => {
  switch (navStyle) {
    case NAV_STYLE_DEFAULT_HORIZONTAL:
      return <HorizontalDefaultMemoized />;
    case NAV_STYLE_DARK_HORIZONTAL:
      return <HorizontalDarkMemoized />;
    case NAV_STYLE_INSIDE_HEADER_HORIZONTAL:
      return <InsideHeaderMemoized />;
    case NAV_STYLE_ABOVE_HEADER:
      return <AboveHeaderMemoized />;
    case NAV_STYLE_BELOW_HEADER:
      return <BelowHeaderMemoized />;
    case NAV_STYLE_FIXED:
      return <TopbarMemoized onSoundUpdate={handleSoundUpdate} />;
    case NAV_STYLE_DRAWER:
      return <TopbarMemoized onSoundUpdate={handleSoundUpdate} />;
    case NAV_STYLE_MINI_SIDEBAR:
      return <TopbarMemoized onSoundUpdate={handleSoundUpdate} />;
    case NAV_STYLE_NO_HEADER_MINI_SIDEBAR:
      return <NoHeaderNotificationMemoized />;
    case NAV_STYLE_NO_HEADER_EXPANDED_SIDEBAR:
      return <NoHeaderNotificationMemoized />;
    default:
      return null;
  }
};

const REPORT_TITLES = {
  depositReport: "Deposit Report",
  withdrawReport: "Withdraw Report",
};

const MainApp = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const { navStyle } = useSelector(({ settings }) => settings);
  const { user } = useSelector(({ auth }) => auth);
  const ACTIVITY_KEY = user._id + "_last_user_activity";
  useIdleLogout(ACTIVITY_KEY, user?.panelIdealTime);

  const streamMeta = useRef({
    reportType: null,
    downloadFormat: null,
    rowCount: 0, // used only to keep "Sr No" continuous across chunks
    started: false,
  });

  const val = useRef({
    value: localStorage.getItem("sound"),
  });

  const handleSoundUpdate = (value) => {
    val.current = { ...val.current, value: !value };
  };

  useEffect(() => {
    window.addEventListener("resize", () => {
      dispatch(updateWindowWidth(window.innerWidth));
    });
  }, [dispatch]);

  const Button = ({ redirectUrl }) => {
    const handleClick = () => {
      history.push(redirectUrl);
    };
    return <a onClick={handleClick}>Click Here</a>;
  };

  const onLogOut = () => {
    localStorage.removeItem("user_id");
    localStorage.removeItem("user");
    localStorage.removeItem("id");
    localStorage.removeItem("user_token");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("status");
    localStorage.removeItem("sortBy");
    localStorage.removeItem(ACTIVITY_KEY);
    dispatch(userSignOut());
    window.location.reload();
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      socket.emit("checkUserSession", {
        authorization: localStorage.getItem("user_token"),
        socketId: socket.id,
      });
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    socket.on("connect", () => {
      console.log("Socket connected");
      socket.emit("joinRoom", { userId: user?._id });
    });
    socket.emit("joinRoom", { userId: user?._id });

    socket.on("sessionExpire", () => {
      console.log("session expired");
      onLogOut();
    });

    handleSocketEvent(
      "newDepositRequest",
      true,
      (newData) => {
        if (newData) {
          if (val.current.value === true) {
            depositSound.play();
            console.log("Sound played");
          }
          const message = (
            <div>
              You have new deposit request of Rs{" "}
              <strong>
                {newData?.depositAccount?.isUniqueTransaction
                  ? newData?.receivedAmount
                  : newData?.amount}
              </strong>{" "}
              from <strong>{newData?.user?.endUserId} </strong>{" "}
              <Button redirectUrl="/deposit/queue" />
            </div>
          );
          return message;
        }
      },
      handleSoundUpdate,
    );

    handleSocketEvent(
      "newWithdrawRequest",
      true,
      (newData) => {
        if (newData) {
          if (val.current.value === true) {
            depositSound.play();
            console.log("Sound played");
          }
          const message = (
            <div>
              You have new{" "}
              {newData?.status === "verified"
                ? "Withdraw Verified Requestd"
                : "Withdraw Pending Requestd"}{" "}
              of Rs <strong>{newData?.amount}</strong> from{" "}
              <strong>{newData?.user?.endUserId}</strong>{" "}
              <Button
                redirectUrl={
                  newData?.status === "verified"
                    ? "/withdraw/queue"
                    : "/withdraw-pending-report"
                }
              />
            </div>
          );
          return message;
        }
      },
      handleSoundUpdate,
    );
    handleSocketEvent(
      "verifyDepositRequest",
      true,
      (newData) => {
        if (newData) {
          if (val.current.value === true) {
            depositSound.play();
          }
          const message = (
            <div>
              You have a request in deposit verification queue{" "}
              <span style={{ fontWeight: "bold" }}>
                {newData?.data?.[0]?.traId}{" "}
              </span>
              <Button redirectUrl={"/deposit-verification-queue"} />
            </div>
          );
          return message;
        }
      },
      handleSoundUpdate,
    );

    handleSocketEvent3(
      "commissionStatusChange",
      (newData) => {
        if (newData) {
          const message = (
            <div>
              {newData[1]?.status === "decline"
                ? "Settlement request declined "
                : newData[1]?.status === "completed"
                  ? "Settlement request approved "
                  : "New settlement request "}
              of Rs <strong>{newData[0]?.receiveAmount}</strong>
              {newData[0]?.status === "submitted" ? " from " : " by "}
              <strong>
                {newData[0]?.status === "submitted"
                  ? newData[0]?.fromData?.name
                  : newData[0]?.toData?.name}{" "}
              </strong>
              <Button redirectUrl="/commission-summary" />
            </div>
          );
          notify.openNotification(message);
          if (val.current.value === true) {
            depositSound.play();
          }
          return message;
        }
      },
      handleSoundUpdate,
    );

    handleSocketEvent2("streaming", (newData) => {
      const chunk = newData?.data?.transaction_data;
      console.log("streaming event received", newData, chunk);
      if (!newData?.streamingState?.statePage || !chunk || chunk.length === 0) {
        return;
      }
      if (!streamMeta.current.started) {
        const downloadFormat = localStorage.getItem("DownloadFormat");
        const reportType = localStorage.getItem("ReportType");
        streamMeta.current = {
          reportType,
          downloadFormat,
          rowCount: 0,
          started: true,
        };
        initExportStream(
          REPORT_TITLES[reportType] || "Report",
          downloadFormat === "Excel" ? "Excel" : "CSV",
        );
      }

      const { reportType, rowCount } = streamMeta.current;

      // Format ONLY this chunk (not the whole dataset) and keep Sr No
      // continuous using the running rowCount as the offset.
      let formattedChunk = [];
      if (reportType === "depositReport") {
        formattedChunk = generateFormattedData(
          chunk,
          "depositReport",
          undefined,
          undefined,
          rowCount,
        );
      } else if (reportType === "withdrawReport") {
        formattedChunk = generateWithdrawReportFormattedData(
          chunk,
          "withdrawReport",
          rowCount,
        );
      }

      appendExportChunk(formattedChunk);
      streamMeta.current.rowCount += chunk.length;

      // `chunk` and `formattedChunk` fall out of scope here and become
      // eligible for GC — nothing large is retained between events.
    });

    handleSocketEvent2("stream-end", (newData) => {
      dispatch(disableButton(false));
      if (newData?.finish) {
        const { reportType } = streamMeta.current;

        if (streamMeta.current.started) {
          finalizeExportStream();
        }

        notify.successNotification(
          "Download complete",
          `Your ${REPORT_TITLES[reportType] || "-"} is download complete!`,
        );

        localStorage.removeItem("DownloadFormat");
        localStorage.removeItem("ReportType");
        streamMeta.current = {
          reportType: null,
          downloadFormat: null,
          rowCount: 0,
          started: false,
        };
      }
    });

    socket.on("error", (error) => {
      console.error("Socket error:", error);
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  return (
    <Layout className="gx-app-layout">
      <AppSidebar navStyle={navStyle} />
      <Layout>
        {getNavStyles(navStyle, handleSoundUpdate)}
        <Content
          className={`gx-layout-content ${getContainerClass(navStyle)} `}
        >
          <App match={match} />
          {/* <Footer>
            <div className="gx-layout-footer-content">{footerText}</div>
          </Footer> */}
        </Content>
      </Layout>
      {/* <Customizer /> */}
    </Layout>
  );
};
export default MainApp;
